import React, { useState, useEffect } from 'react';
import {
  getNotificationPermission,
  requestFCMToken,
  isNotificationSupported,
  needsExplicitPermissionUi,
  saveTokenToBackend,
  setupTokenRefresh
} from '../../push/firebaseUtils';

const styles = {
  container: {
    backgroundColor: '#007bff',
    color: '#fff',
    padding: '12px 16px',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10000,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  message: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  icon: {
    fontSize: '16px'
  },
  text: {
    fontSize: '14px',
    fontWeight: '500'
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  enableButton: {
    backgroundColor: '#fff',
    color: '#007bff',
    border: 'none',
    borderRadius: '4px',
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  dismissButton: {
    backgroundColor: 'transparent',
    color: '#fff',
    border: 'none',
    fontSize: '18px',
    cursor: 'pointer',
    padding: '2px 6px',
    borderRadius: '2px',
    lineHeight: '1'
  }
};

/** Safari / iOS: user taps Enable before system permission (footyleague pattern). */
const NotificationBar = ({ onTokenReceived }) => {
  const [showBar, setShowBar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isNotificationSupported() || !needsExplicitPermissionUi()) {
      return;
    }
    if (getNotificationPermission() !== 'default') {
      return;
    }
    const dismissed = localStorage.getItem('notification-bar-dismissed');
    if (dismissed) {
      if (new Date() <= new Date(dismissed)) {
        return;
      }
      localStorage.removeItem('notification-bar-dismissed');
    }
    setShowBar(true);
  }, []);

  const handleEnableNotifications = async () => {
    setIsLoading(true);
    try {
      const token = await requestFCMToken();
      await saveTokenToBackend(token);
      setupTokenRefresh(onTokenReceived);
      onTokenReceived && onTokenReceived(token);
      setShowBar(false);
    } catch (error) {
      console.error('[push] Error requesting FCM token from bar:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = () => {
    setShowBar(false);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    localStorage.setItem('notification-bar-dismissed', tomorrow.toISOString());
  };

  if (!showBar) {
    return null;
  }

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <div style={styles.message}>
          <span style={styles.icon}>🔔</span>
          <span style={styles.text}>Enable notifications to receive important updates</span>
        </div>
        <div style={styles.actions}>
          <button
            type="button"
            onClick={handleEnableNotifications}
            disabled={isLoading}
            style={styles.enableButton}
          >
            {isLoading ? 'Enabling...' : 'Enable'}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            style={styles.dismissButton}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationBar;
